import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { NativeSelect } from "@/components/ui/native-select";
import { Pagination } from "@/components/ui/pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import PageHeader from "../PageHeader";
import usePageParam from "../../hooks/usePageParam";
import { documentTagApi } from "../../services/api";
import { apiErrorMessage } from "../../services/apiError";

// "" (All) can't be a ToggleGroup value: Radix represents "nothing selected"
// as "" too, which would make the re-click deselect-guard swallow it. ALL_
// is a sentinel used only at the toggle boundary; `filter` itself (what the
// fetch and tests see) stays "" for All, same as before the port.
const ALL_FILTER = "all";

const FILTERS = [
  { value: "suggested", label: "To review" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "", label: "All" },
];

const STATUS_BADGE = {
  suggested: { variant: "outline", label: "suggested" },
  approved: { variant: "success", label: "approved" },
  rejected: { variant: "secondary", label: "rejected" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_BADGE[status] || STATUS_BADGE.suggested;
  return <Badge variant={cfg.variant}>{cfg.label}</Badge>;
}

StatusBadge.propTypes = { status: PropTypes.string };

const PAGE_SIZE = 12; // mirrors backend REST_FRAMEWORK PAGE_SIZE
// Safety cap when walking every page for the merge-target list — 20 pages =
// 240 tags, far beyond a personal vocabulary. Prevents a runaway loop if the
// API ever misbehaves.
const MAX_TARGET_PAGES = 20;

// The merge-target dropdown needs the WHOLE vocabulary, not one page of it —
// a missing target silently makes a merge impossible (L4 review finding).
async function fetchAllTagPages() {
  const all = [];
  for (let page = 1; page <= MAX_TARGET_PAGES; page += 1) {
    const response = await documentTagApi.getAll({ page });
    all.push(...(response.data.results || []));
    if (!response.data.next) break;
  }
  return all;
}

function TagReview() {
  const [filter, setFilter] = useState("suggested");
  const [tags, setTags] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = usePageParam();
  const [allTags, setAllTags] = useState([]); // merge targets
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [mergeTargets, setMergeTargets] = useState({}); // tag id → target id

  const fetchTags = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page };
      if (filter) params.status = filter;
      const [filtered, everything] = await Promise.all([
        documentTagApi.getAll(params),
        fetchAllTagPages(),
      ]);
      setTags(filtered.data.results || []);
      setCount(filtered.data.count || 0);
      setAllTags(everything);
      // Drop merge selections whose target vanished or was rejected since —
      // a stale id would keep the Merge button live against a dead target.
      const validTargets = new Set(
        everything
          .filter((t) => t.status !== "rejected")
          .map((t) => String(t.id)),
      );
      setMergeTargets((prev) =>
        Object.fromEntries(
          Object.entries(prev).filter(([, target]) =>
            validTargets.has(String(target)),
          ),
        ),
      );
    } catch (err) {
      if (err?.response?.status === 404 && page > 1) {
        // the page evaporated (approvals shrank the queue, or an old link)
        setPage(1, { replace: true });
        return;
      }
      setError(apiErrorMessage(err, "Failed to load tags."));
    } finally {
      setLoading(false);
    }
  }, [filter, page, setPage]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  const handleFilterChange = (next) => {
    setFilter(next);
    setPage(1, { replace: true });
  };

  const handleToggleChange = (next) => {
    if (!next) return; // re-click guard: a deselect can't happen either way
    handleFilterChange(next === ALL_FILTER ? "" : next);
  };

  const act = async (tag, action) => {
    setBusyId(tag.id);
    setActionError(null);
    try {
      await action();
      await fetchTags();
    } catch (err) {
      setActionError(apiErrorMessage(err, "That didn't work."));
    } finally {
      setBusyId(null);
    }
  };

  const handleApprove = (tag) => act(tag, () => documentTagApi.approve(tag.id));
  const handleReject = (tag) => act(tag, () => documentTagApi.reject(tag.id));
  const handleMerge = (tag) => {
    const intoId = mergeTargets[tag.id];
    if (!intoId) return;
    act(tag, () => documentTagApi.merge(tag.id, Number(intoId)));
  };

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  return (
    <div className="page-container mt-6 pt-4" style={{ maxWidth: "860px" }}>
      <PageHeader
        title="Tags"
        subtitle="The AI suggests tags; nothing sticks until you approve it. Approved tags guide future suggestions."
      />

      <ToggleGroup
        type="single"
        variant="outline"
        size="sm"
        value={filter || ALL_FILTER}
        onValueChange={handleToggleChange}
        aria-label="Filter tags by status"
        className="mb-4"
      >
        {FILTERS.map((f) => (
          <ToggleGroupItem
            key={f.value || ALL_FILTER}
            value={f.value || ALL_FILTER}
          >
            {f.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>

      {actionError && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{actionError}</AlertDescription>
        </Alert>
      )}

      {loading && (
        <div className="py-12 text-center" role="status">
          <Loader2 className="inline size-6 animate-spin text-muted-foreground" />
          <span className="sr-only">Loading tags…</span>
        </div>
      )}

      {!loading && error && (
        <Alert variant="destructive">
          <AlertDescription className="flex items-center gap-3">
            <span className="flex-1">{error}</span>
            <Button size="sm" variant="outline" onClick={fetchTags}>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {!loading && !error && tags.length === 0 && (
        <div className="py-12 text-center text-muted-foreground">
          {filter === "suggested"
            ? "Nothing to review — new suggestions appear here as you add documents."
            : "No tags here yet."}
        </div>
      )}

      {!loading && !error && tags.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tag</TableHead>
              <TableHead>Documents</TableHead>
              <TableHead>Status</TableHead>
              <TableHead style={{ width: "45%" }}>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tags.map((tag) => {
              const busy = busyId === tag.id;
              const targets = allTags.filter(
                (t) => t.id !== tag.id && t.status !== "rejected",
              );
              return (
                <TableRow key={tag.id}>
                  <TableCell>
                    <span className="font-semibold">{tag.name}</span>
                    {tag.origin === "llm" && (
                      <span className="ml-2 text-sm text-muted-foreground">
                        AI
                      </span>
                    )}
                  </TableCell>
                  <TableCell>{tag.document_count}</TableCell>
                  <TableCell>
                    <StatusBadge status={tag.status} />
                  </TableCell>
                  <TableCell className="whitespace-normal">
                    <div className="flex flex-wrap items-center gap-2">
                      {tag.status !== "approved" && (
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={busy}
                          onClick={() => handleApprove(tag)}
                        >
                          Approve
                        </Button>
                      )}
                      {tag.status !== "rejected" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                          disabled={busy}
                          onClick={() => handleReject(tag)}
                          title="Detaches the tag everywhere and stops it being suggested again"
                        >
                          Reject
                        </Button>
                      )}
                      {targets.length > 0 && tag.status !== "rejected" && (
                        <>
                          <NativeSelect
                            className="h-8 w-auto min-w-[10rem]"
                            value={mergeTargets[tag.id] || ""}
                            onChange={(event) =>
                              setMergeTargets((prev) => ({
                                ...prev,
                                [tag.id]: event.target.value,
                              }))
                            }
                            aria-label={`Merge ${tag.name} into`}
                          >
                            <option value="">Merge into…</option>
                            {targets.map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.name}
                              </option>
                            ))}
                          </NativeSelect>
                          {mergeTargets[tag.id] && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={busy}
                              onClick={() => handleMerge(tag)}
                            >
                              Merge
                            </Button>
                          )}
                        </>
                      )}
                      {busy && (
                        <Loader2 className="size-4 animate-spin text-muted-foreground" />
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {!loading && !error && totalPages > 1 && (
        <div className="mt-4 flex justify-center">
          <Pagination page={page} total={totalPages} onChange={setPage} />
        </div>
      )}
    </div>
  );
}

export default TagReview;
